/**
 * Hook for handling file import operations
 */

import { useState, useCallback } from 'react'
import type { ImportPreview, ImportOptions } from '../types/financials.types'
import { parseFile } from '../utils/fileParser'

export const useFileImport = () => {
	const [isImporting, setIsImporting] = useState(false)
	const [importProgress, setImportProgress] = useState(0)
	const [importError, setImportError] = useState<Error | null>(null)
	const [preview, setPreview] = useState<ImportPreview | null>(null)

	/**
	 * Upload and parse a file
	 */
	const uploadFile = useCallback(async (file: File): Promise<ImportPreview> => {
		setIsImporting(true)
		setImportProgress(10)
		setImportError(null)

		try {
			// Validate file size (10MB limit)
			const maxSize = 10 * 1024 * 1024 // 10MB
			if (file.size > maxSize) {
				throw new Error(`File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds maximum of 10MB`)
			}

			setImportProgress(30)

			// Parse file
			const importPreview = await parseFile(file)

			setImportProgress(100)
			setPreview(importPreview)
			setIsImporting(false)

			return importPreview
		} catch (error) {
			const err = error instanceof Error ? error : new Error('Failed to parse file')
			setImportError(err)
			setIsImporting(false)
			setImportProgress(0)
			throw err
		}
	}, [])

	/**
	 * Confirm and execute the import
	 */
	const confirmImport = useCallback(
		async (importPreview: ImportPreview, options: ImportOptions, onComplete: (data: any) => void) => {
			setIsImporting(true)
			setImportProgress(10)
			setImportError(null)

			try {
				setImportProgress(50)

				// TODO: Call API to save imported data
				// This would be implemented when we create the Redux slice
				// For now, just simulate success

				await new Promise(resolve => setTimeout(resolve, 500))

				setImportProgress(100)
				setIsImporting(false)
				setPreview(null)

				onComplete({
					preview: importPreview,
					options,
				})
			} catch (error) {
				const err = error instanceof Error ? error : new Error('Failed to import data')
				setImportError(err)
				setIsImporting(false)
				setImportProgress(0)
				throw err
			}
		},
		[]
	)

	/**
	 * Cancel the import
	 */
	const cancelImport = useCallback(() => {
		setIsImporting(false)
		setImportProgress(0)
		setImportError(null)
		setPreview(null)
	}, [])

	return {
		isImporting,
		importProgress,
		importError,
		preview,
		uploadFile,
		confirmImport,
		cancelImport,
	}
}

export type UseFileImportReturn = ReturnType<typeof useFileImport>

